import { useEffect, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { auth, i18n, refreshSession } from '../lib/api.js'
import type { ApiError } from '../lib/api.js'
import { setToken, clearToken, isAuthenticated, setStoredTenantSlug } from '../lib/auth.js'
import { homePathFor, isPickerRequest, resolveHomePath } from '../lib/session.js'
import { Button, Input, Select, Spinner, toast } from '../components/ui/index.js'
import { PratzuLogo } from '../components/layout/Shell.js'
import { useI18n } from '../lib/i18n.js'
import { LIVE_PRODUCTION_URLS, resolveSessionWithin, sameSiteApiUrl } from '@self-restaurant/shared'

// Staging (#118): VITE_API_URL aponta para a API de staging no build.
// Produção: api.pratzu.com; em admin.asknamba.com (paralelo) a API do mesmo site.
const API_ORIGIN = sameSiteApiUrl(
  (import.meta.env.VITE_API_URL as string | undefined) ?? LIVE_PRODUCTION_URLS.api,
  typeof window !== 'undefined' ? window.location.hostname : undefined,
)

/** Teto do loading inicial do /login (refresh pendurado → mostra o formulário). */
const SESSION_RESOLVE_TIMEOUT_MS = 10_000

type Step = 'email' | 'otp' | 'choose'

type Membership = {
  tenant_id: string
  tenant_slug: string
  tenant_name: string
  roles: string[]
}

// Rótulos de papel — chaves i18n (papel desconhecido cai no próprio slug)
const ROLE_LABELS: Record<string, string> = {
  platform_owner: 'admin.login.role_platform_owner',
  restaurant_owner: 'admin.login.role_owner',
  kitchen: 'admin.nav.kitchen',
  waiter: 'admin.nav.waiter',
}

function GoogleIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 48 48" aria-hidden="true">
      <path
        fill="#EA4335"
        d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z"
      />
      <path
        fill="#4285F4"
        d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z"
      />
      <path
        fill="#FBBC05"
        d="M10.53 28.59c-.48-1.45-.76-2.99-.76-4.59s.27-3.14.76-4.59l-7.98-6.19C.92 16.46 0 20.12 0 24c0 3.88.92 7.54 2.56 10.78l7.97-6.19z"
      />
      <path
        fill="#34A853"
        d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.15 1.45-4.92 2.3-8.16 2.3-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z"
      />
    </svg>
  )
}

export function Login() {
  const navigate = useNavigate()
  const { t, lang, setLang } = useI18n()
  const [step, setStep] = useState<Step>('email')
  const [email, setEmail] = useState('')
  const [otp, setOtp] = useState('')
  const [loading, setLoading] = useState(false)
  const [memberships, setMemberships] = useState<Membership[] | null>(null)
  // Seletor aberto com uma sessão já existente (#319): o select precisa do
  // slug do tenant do token como contexto (fora de /r/:slug)
  const pickerContextSlug = useRef<string | undefined>(undefined)
  const [googleEnabled, setGoogleEnabled] = useState(false)
  // Sessão em resolução (retorno do Google, refresh em andamento ou login
  // concluído navegando para o painel): mostra loading em vez do formulário,
  // para o /login nunca "piscar" depois de um login bem-sucedido.
  // Começa SEMPRE resolvendo (QA #290): só há cookie de refresh (httpOnly,
  // invisível ao JS) e nenhum access token → o refresh single-flight decide
  // antes do primeiro paint; o formulário só aparece se o refresh falhar.
  // Exceção: retorno de erro do Google (sr_auth_error) mostra o formulário.
  const [resolving, setResolving] = useState<boolean>(
    () => !/(?:^#|&)sr_auth_error=/.test(window.location.hash),
  )

  // Sessão já existente (hotfix #288): token válido ou refresh bem-sucedido
  // → entra direto no painel (não fica parado no /login).
  useEffect(() => {
    if (/(?:^#|&)sr_auth(?:_error)?=/.test(window.location.hash)) return
    let alive = true
    // #319: destino pelo tenant da sessão (/auth/session), não pelo slug
    // guardado; vários restaurantes → seletor; tenant apagado → formulário.
    const goHome = async () => {
      if (isPickerRequest(window.location.search) && (await openPicker())) return
      const path = await resolveHomePath()
      if (!alive) return
      if (path && !path.startsWith('/login')) {
        navigate(path, { replace: true })
      } else if (path) {
        if (!(await openPicker())) setResolving(false)
      } else {
        clearToken()
        setResolving(false)
      }
    }
    const openPicker = async (): Promise<boolean> => {
      try {
        const s = await auth.session()
        // contexto do select = tenant do token: o do vínculo atual ou, no
        // token pós-OTP da plataforma (sem vínculo nela), o tenant_slug
        const contextSlug = s.current?.tenant_slug ?? s.tenant_slug
        if (!alive || !contextSlug || s.memberships.length === 0) return false
        pickerContextSlug.current = contextSlug
        setMemberships(s.memberships)
        setStep('choose')
        setResolving(false)
        return true
      } catch {
        return false
      }
    }
    setResolving(true)
    if (isAuthenticated()) {
      void goHome()
      return () => {
        alive = false
      }
    }
    // #291: resolve SEMPRE (sucesso, 401, erro de rede ou timeout) — o
    // loading inicial nunca fica preso; o formulário só aparece na falha.
    void resolveSessionWithin(refreshSession, SESSION_RESOLVE_TIMEOUT_MS).then((ok) => {
      if (!alive) return
      if (ok && isAuthenticated()) void goHome()
      else setResolving(false)
    })
    return () => {
      alive = false
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // Retorno do login Google: a API volta para /login#sr_auth=<token>.
  useEffect(() => {
    const authMatch = window.location.hash.match(/(?:^#|&)sr_auth=([^&]+)/)
    const errMatch = window.location.hash.match(/(?:^#|&)sr_auth_error=([^&]+)/)
    if (!authMatch && !errMatch) return
    history.replaceState(null, '', window.location.pathname + window.location.search)
    if (authMatch?.[1]) {
      setResolving(true)
      void handleGoogleToken(decodeURIComponent(authMatch[1]))
    } else if (errMatch?.[1]) {
      toast.error(t('admin.login.google_err'))
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    fetch(`${import.meta.env.DEV ? '' : API_ORIGIN}/auth/google/enabled`)
      .then((r) => r.json())
      .then((d: { data?: { google_sso_enabled?: boolean } }) => {
        if (d?.data?.google_sso_enabled) setGoogleEnabled(true)
      })
      .catch(() => {
        /* SSO indisponível — login por OTP segue normal */
      })
  }, [])

  function startGoogleLogin() {
    const returnTo = window.location.origin + '/login'
    // Origem da API DIRETA (não o caminho relativo): o proxy do admin segue
    // redirects internamente e engoliria o 302 para o Google. Token inicial
    // no tenant platform; o seletor de memberships escolhe o destino.
    const apiOrigin = API_ORIGIN
    window.location.href = `${apiOrigin}/auth/google/start?tenant=platform&return_to=${encodeURIComponent(returnTo)}`
  }

  async function handleGoogleToken(token: string) {
    setLoading(true)
    try {
      setToken(token)
      // false = não navegou (seletor de memberships ou sem acesso)
      if (!(await enterWithMemberships())) setResolving(false)
    } catch (err) {
      clearToken()
      setResolving(false)
      toast.error(err instanceof Error ? err.message : t('admin.login.google_failed'))
    } finally {
      setLoading(false)
    }
  }

  /** true se entrou no painel (navegou). */
  async function enterWithMemberships(): Promise<boolean> {
    // token novo (OTP/Google): o contexto do select volta a ser o da página
    pickerContextSlug.current = undefined
    const list = await auth.memberships()
    if (list.length === 0) {
      clearToken()
      toast.error(t('admin.login.no_access'))
      return false
    }
    if (list.length === 1 && list[0]!.roles.length === 1) {
      return selectMembership(list[0]!, list[0]!.roles[0]!)
    }
    setMemberships(list)
    setStep('choose')
    return false
  }

  async function handleRequestOtp(e: React.FormEvent) {
    e.preventDefault()
    if (!email.trim()) return
    setLoading(true)
    try {
      await auth.requestOtp(email.trim())
      setStep('otp')
      toast.success(t('admin.login.code_sent_to', { email }))
    } catch (err) {
      toast.error(err instanceof Error ? err.message : t('admin.login.send_code_err'))
    } finally {
      setLoading(false)
    }
  }

  async function handleVerifyOtp(e: React.FormEvent) {
    e.preventDefault()
    if (otp.length !== 6) return
    setLoading(true)
    try {
      const result = await auth.verifyOtp(email.trim(), otp.trim())
      // memberships/select exigem o token emitido no verify — guardá-lo antes
      setToken(result.access_token)
      await enterWithMemberships()
    } catch (err) {
      toast.error(err instanceof Error ? err.message : t('admin.login.invalid_code'))
    } finally {
      setLoading(false)
    }
  }

  async function selectMembership(m: Membership, role: string): Promise<boolean> {
    setLoading(true)
    try {
      const result = await auth.select(m.tenant_id, role, pickerContextSlug.current)
      setResolving(true)
      setToken(result.access_token)
      setStoredTenantSlug(m.tenant_slug)
      // replace: o /login não fica no histórico (voltar não reabre o login)
      navigate(homePathFor(result.user.role, m.tenant_slug), { replace: true })
      return true
    } catch (err) {
      setResolving(false)
      const apiErr = err as ApiError
      toast.error(apiErr.message ?? t('admin.login.signin_err'))
      if (apiErr.status === 401) {
        setStep('email')
        setMemberships(null)
      }
      return false
    } finally {
      setLoading(false)
    }
  }

  if (resolving) {
    return (
      <div
        data-testid="login-resolving"
        className="flex items-center justify-center min-h-screen bg-pratzu-cream"
      >
        <Spinner className="w-8 h-8" />
      </div>
    )
  }

  return (
    <div
      data-testid="login-page"
      className="min-h-screen flex items-center justify-center bg-pratzu-cream p-4"
    >
      <div className="w-full max-w-md">
        {/* Idioma — mesmo catálogo do painel; a escolha vale cross-app (cookie) */}
        <div className="flex justify-end mb-3">
          <div className="w-40">
            <LangSelect lang={lang} onChange={setLang} ariaLabel={t('admin.nav.language')} />
          </div>
        </div>
        {/* Logo — PRATZU (o h1 acessível é o alt da imagem) */}
        <div className="flex flex-col items-center mb-8">
          <h1 className="m-0">
            <PratzuLogo className="h-12 w-auto" />
          </h1>
          <p className="text-sm text-pratzu-plum font-semibold mt-3">{t('admin.login.subtitle')}</p>
        </div>

        <div className="bg-white rounded-2xl border-2 border-pratzu-aubergine shadow-tm-pop p-8">
          {step === 'email' ? (
            <>
              <h2 className="text-lg font-display text-pratzu-aubergine mb-1">
                {t('admin.login.title')}
              </h2>
              <p className="text-sm text-gray-500 mb-6">{t('admin.login.email_hint')}</p>
              {googleEnabled && (
                <>
                  <button
                    type="button"
                    data-testid="login-google"
                    onClick={startGoogleLogin}
                    className="w-full mb-4 inline-flex items-center justify-center gap-2.5 rounded-xl border border-gray-300 bg-white px-4 py-3 text-sm font-semibold text-gray-800 hover:bg-gray-50 transition-colors"
                  >
                    <GoogleIcon /> {t('admin.login.google_btn')}
                  </button>
                  <div className="flex items-center gap-3 mb-4">
                    <span className="flex-1 h-px bg-gray-200" />
                    <span className="text-xs text-gray-400">{t('admin.login.or')}</span>
                    <span className="flex-1 h-px bg-gray-200" />
                  </div>
                </>
              )}
              <form onSubmit={handleRequestOtp} className="space-y-4">
                <Input
                  label={t('admin.login.email')}
                  data-testid="login-email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder={t('admin.login.email_ph')}
                  autoFocus
                  required
                />
                <Button
                  type="submit"
                  loading={loading}
                  className="w-full"
                  size="lg"
                  data-testid="login-send-code"
                >
                  {t('admin.login.send_code')}
                </Button>
              </form>
            </>
          ) : step === 'otp' ? (
            <>
              <h2 className="text-lg font-display text-pratzu-aubergine mb-1">
                {t('admin.login.verify_title')}
              </h2>
              <p className="text-sm text-gray-500 mb-6">
                {t('admin.login.code_sent_prefix')} <strong>{email}</strong>
              </p>
              <form onSubmit={handleVerifyOtp} className="space-y-4">
                <Input
                  label={t('admin.login.code_label')}
                  type="text"
                  data-testid="login-code"
                  inputMode="numeric"
                  pattern="[0-9]{6}"
                  maxLength={6}
                  value={otp}
                  onChange={(e) => setOtp(e.target.value.replace(/\D/g, ''))}
                  placeholder="000000"
                  autoFocus
                  required
                />
                <Button
                  type="submit"
                  loading={loading}
                  className="w-full"
                  size="lg"
                  data-testid="login-verify"
                >
                  {t('admin.login.title')}
                </Button>
                <button
                  type="button"
                  data-testid="login-use-another-email"
                  onClick={() => {
                    setStep('email')
                    setOtp('')
                  }}
                  className="w-full text-sm text-gray-500 hover:text-gray-700 underline"
                >
                  {t('admin.login.use_another_email')}
                </button>
              </form>
            </>
          ) : (
            <>
              <h2 className="text-lg font-display text-pratzu-aubergine mb-1">
                {t('admin.login.choose_title')}
              </h2>
              {/* seletor aberto com sessão existente (#319) não tem o e-mail digitado */}
              {email && (
                <p className="text-sm text-gray-500 mb-6">
                  {t('admin.login.choose_hint')} <strong>{email}</strong>?
                </p>
              )}
              <div className="space-y-3 max-h-96 overflow-y-auto">
                {memberships?.map((m) => (
                  <div
                    key={m.tenant_id}
                    data-testid={`login-membership-${m.tenant_id}`}
                    className="rounded-xl border border-gray-200 p-4"
                  >
                    <p className="font-semibold text-gray-900 mb-2">{m.tenant_name}</p>
                    <div className="flex flex-wrap gap-2">
                      {m.roles.map((role) => (
                        <button
                          key={role}
                          type="button"
                          disabled={loading}
                          data-testid={`login-select-${m.tenant_id}-${role}`}
                          onClick={() => void selectMembership(m, role)}
                          className="inline-flex items-center gap-1.5 rounded-lg border border-indigo-200 bg-indigo-50 px-3 py-2 text-sm font-medium text-indigo-700 hover:bg-indigo-100 transition-colors disabled:opacity-50"
                        >
                          {role === 'kitchen' && '🔥'}
                          {role === 'waiter' && '🔔'}
                          {role === 'restaurant_owner' && '👤'}
                          {role === 'platform_owner' && '🌐'}
                          {t('admin.login.enter_as', { role: t(ROLE_LABELS[role] ?? role) })}
                        </button>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
              <button
                type="button"
                data-testid="login-use-another-email"
                onClick={() => {
                  setStep('email')
                  setOtp('')
                  setMemberships(null)
                }}
                className="mt-4 w-full text-sm text-gray-500 hover:text-gray-700 underline"
              >
                {t('admin.login.use_another_email')}
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  )
}

/** Seletor de idioma da tela de login — catálogo público /admin/i18n/locales. */
function LangSelect({
  lang,
  onChange,
  ariaLabel,
}: {
  lang: string
  onChange: (lang: string) => void
  ariaLabel: string
}) {
  const { data: catalogLocales = [] } = useQuery({
    queryKey: ['catalog-locales'],
    queryFn: i18n.locales,
    staleTime: 5 * 60_000,
    retry: 1,
  })
  const options = catalogLocales.map((l) => ({ value: l.code, label: l.native }))
  if (options.length < 2) return null
  return (
    <Select
      aria-label={ariaLabel}
      value={lang}
      onChange={(e) => onChange(e.target.value)}
      options={options}
    />
  )
}
